import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Observable } from 'rxjs';

import {
  KsDebitOrderAccountPayload,
  KsSubmitResponse,
} from '../../../../core/kickstart/kickstart.types';
import { ModalComponent } from '../../../../shared/modal/modal';
import { placeRefusal } from '../../../../shared/modal/modal-refusal';

export interface DebitOrderAccountDialogData {
  readonly heading?: string;
  readonly submitLabel?: string;
  readonly bankLabel?: string;
  readonly message: string;
  readonly confirm: (account: KsDebitOrderAccountPayload) => Observable<KsSubmitResponse>;
}

/**
 * KS-OB-07 with its account (DOC-88 UI-DA-88-01): the client confirms the mandate
 * and names the account the debit order will run on — the bank and the last four
 * digits. Nothing is pre-filled or suggested from the payout account or anything
 * else on the file, and nothing here checks it. Closes only once the confirmation
 * is recorded, with the server's answer.
 */
@Component({
  selector: 'app-debit-order-account-dialog',
  imports: [ModalComponent, MatFormFieldModule, MatInputModule, ReactiveFormsModule],
  template: `
    <app-modal
      [heading]="data.heading ?? 'Confirm the debit order mandate?'"
      [description]="data.message"
      [submitLabel]="data.submitLabel ?? 'Confirm debit order'"
      [formGroup]="form"
      [pending]="pending()"
      [error]="error()"
      (submitted)="save()"
    >
      <div class="modal-fields">
        <mat-form-field appearance="outline">
          <mat-label>{{ data.bankLabel ?? 'Bank' }}</mat-label>
          <input matInput formControlName="bank_name" data-testid="debit-bank" />
          @if (form.controls.bank_name.hasError('serverError')) {
            <mat-error>{{ form.controls.bank_name.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Enter the bank.</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Last four digits of the account</mat-label>
          <input
            matInput
            formControlName="last_four"
            inputmode="numeric"
            maxlength="4"
            data-testid="debit-last-four"
          />
          @if (form.controls.last_four.hasError('serverError')) {
            <mat-error>{{ form.controls.last_four.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Enter the last four digits of the account.</mat-error>
          }
        </mat-form-field>
      </div>
    </app-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DebitOrderAccountDialogComponent {
  protected readonly data = inject<DebitOrderAccountDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<DebitOrderAccountDialogComponent, KsSubmitResponse>>(MatDialogRef);

  protected readonly form = inject(FormBuilder).nonNullable.group({
    bank_name: ['', [Validators.required, Validators.pattern(/\S/)]],
    last_four: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
  });
  protected readonly pending = signal(false);
  protected readonly error = signal<string | null>(null);

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    this.pending.set(true);
    this.error.set(null);
    this.data.confirm({ bank_name: v.bank_name.trim(), last_four: v.last_four }).subscribe({
      next: (res) => this.dialogRef.close(res),
      error: (err: unknown) => {
        this.pending.set(false);
        this.error.set(placeRefusal(this.form, err, 'Could not confirm. Please try again.'));
      },
    });
  }
}
